import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Shield, Clock, Users, Award, Star, ArrowRight, MessageCircle } from 'lucide-react';

const WhyChooseSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [counters, setCounters] = useState({ clients: 0, satisfaction: 0, experience: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Start counter animations
          animateCounters();
        }
      },
      { threshold: 0.1 } // Reduced threshold for mobile
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const animateCounters = () => {
    const targets = { clients: 500, satisfaction: 98, experience: 15 };
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setCounters({
        clients: Math.floor(targets.clients * progress),
        satisfaction: Math.floor(targets.satisfaction * progress),
        experience: Math.floor(targets.experience * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounters(targets);
      }
    }, stepTime);
  };

  const features = [
    {
      icon: Shield,
      title: 'Intervention à domicile sans surcoût',
      description: 'Service mobile gratuit dans toute la ville',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100'
    },
    {
      icon: Award,
      title: 'Pièces et huiles de qualité premium',
      description: 'Partenariat Shell et pièces certifiées',
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'from-yellow-50 to-orange-100'
    },
    {
      icon: Users,
      title: 'Techniciens formés et professionnels',
      description: 'Équipe certifiée avec 15+ années d\'expérience',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'from-green-50 to-emerald-100'
    },
    {
      icon: MessageCircle,
      title: 'Suivi client humain et réactif',
      description: 'Support WhatsApp avec réponse en moins de 5 minutes',
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'from-purple-50 to-indigo-100'
    },
    {
      icon: Clock,
      title: 'Facture et reporting pour entreprises',
      description: 'Solutions B2B adaptées avec suivi détaillé',
      color: 'from-pink-500 to-rose-600',
      bgColor: 'from-pink-50 to-rose-100'
    }
  ];

  const stats = [
    {
      value: counters.clients,
      suffix: '+',
      label: 'Clients satisfaits',
      icon: '👥',
      color: 'text-blue-600'
    },
    {
      value: counters.satisfaction,
      suffix: '%',
      label: 'Taux de satisfaction',
      icon: '⭐',
      color: 'text-green-600'
    },
    {
      value: counters.experience,
      suffix: '+',
      label: 'Années d\'expérience',
      icon: '🏆',
      color: 'text-purple-600'
    }
  ];

  const testimonialHighlights = [
    {
      quote: "Service exceptionnel !",
      author: "Ahmed K.",
      rating: 5
    },
    {
      quote: "Très professionnel",
      author: "Sophia M.",
      rating: 5
    },
    {
      quote: "Je recommande vivement",
      author: "Karim B.",
      rating: 5
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden min-h-screen"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Background Elements */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-3 py-2 sm:px-4 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
            <CheckCircle className="w-4 h-4 mr-2" />
            Nos avantages
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            Pourquoi choisir{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Faucon Bleu
            </span>{' '}
            ?
          </h2>
          
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Découvrez ce qui fait de nous le leader du service automobile mobile à Casablanca. 
            Qualité, rapidité et satisfaction client garanties.
          </p>
        </div>

        {/* Stats Section */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 transform hover:scale-105 transition-all duration-300"
            >
              <div className="text-2xl sm:text-3xl lg:text-4xl mb-2 sm:mb-4">{stat.icon}</div>
              <div className={`text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold ${stat.color} mb-1 sm:mb-2`}>
                {stat.value}{stat.suffix}
              </div>
              <div className="text-sm sm:text-base text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-start">
          
          {/* Features List */}
          <div className={`space-y-4 sm:space-y-6 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`} itemProp="hasOfferCatalog" itemScope itemType="https://schema.org/OfferCatalog">
            
            <div className="space-y-3 sm:space-y-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                const isActive = index === activeFeature;
                
                return (
                  <div
                    key={index}
                    className={`
                      group relative p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-500 cursor-pointer
                      ${isActive 
                        ? `bg-gradient-to-r ${feature.color} text-white border-transparent shadow-xl scale-105` 
                        : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-lg'
                      }
                    `}
                    onMouseEnter={() => setActiveFeature(index)}
                    itemProp="itemListElement" 
                    itemScope 
                    itemType="https://schema.org/Offer"
                  >
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className={`
                        flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300
                        ${isActive ? 'bg-white/20 scale-110' : `bg-gradient-to-r ${feature.bgColor}`}
                      `}>
                        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 ${isActive ? 'text-white' : 'text-blue-600'}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 
                          className={`text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2 ${isActive ? 'text-white' : 'text-gray-900'}`}
                          itemProp="name"
                        >
                          {feature.title}
                        </h3>
                        <p 
                          className={`text-sm sm:text-base ${isActive ? 'text-white/90' : 'text-gray-600'}`}
                          itemProp="description"
                        >
                          {feature.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full animate-pulse shadow-lg" />
                    )}
                    
                    {/* Background pattern */}
                    <div className={`
                      absolute inset-0 opacity-10 rounded-xl sm:rounded-2xl transition-opacity duration-500
                      ${isActive ? 'opacity-20' : 'opacity-0 group-hover:opacity-10'}
                    `}>
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-transparent" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual Content */}
          <div className={`relative transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            
            {/* Logo & Brand */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-block p-4 sm:p-6 bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100">
                <img 
                  src="/logo.jpeg" 
                  alt="Logo Faucon Bleu - Service automobile mobile"
                  className="h-16 sm:h-20 w-auto mx-auto"
                  loading="lazy"
                  itemProp="logo"
                />
              </div>
            </div>

            {/* Customer Testimonials Mini Cards */}
            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 text-center">Avis clients récents</h4>
              <div className="grid gap-2 sm:gap-3">
                {testimonialHighlights.map((testimonial, index) => (
                  <div 
                    key={index}
                    className={`
                      p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100
                      transition-all duration-500 hover:shadow-md hover:scale-105
                      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                    `}
                    style={{ transitionDelay: `${(index + 8) * 200}ms` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700">{testimonial.author}</span>
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 italic">"{testimonial.quote}"</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Partnership Showcase */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-yellow-200 mb-6 sm:mb-8">
              <div className="text-center">
                <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Partenaire officiel</h4>
                <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                  <img 
                    src="/images/shell.png" 
                    alt="Shell - Partenaire officiel" 
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full"
                    loading="lazy"
                  />
                  <div className="text-left">
                    <div className="font-bold text-gray-900 text-sm sm:text-base">Shell</div>
                    <div className="text-xs sm:text-sm text-gray-600">Huiles premium</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="text-center p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100">
                <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">🏆</div>
                <div className="font-semibold text-gray-900 text-xs sm:text-sm">Qualité certifiée</div>
                <div className="text-xs text-gray-600">Normes professionnelles</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100">
                <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">🔒</div>
                <div className="font-semibold text-gray-900 text-xs sm:text-sm">Assurance</div>
                <div className="text-xs text-gray-600">Véhicules couverts</div>
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp CTA Section */}
        <div className={`mt-12 sm:mt-16 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`} itemScope itemType="https://schema.org/ContactPoint">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-white relative overflow-hidden">
            
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/10" />
            </div>
            
            <div className="relative z-10 grid lg:grid-cols-2 gap-6 sm:gap-8 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 sm:mb-4" itemProp="name">
                  Une question ? Besoin d'un devis ?
                </h3>
                <p className="text-sm sm:text-base lg:text-xl text-green-100 mb-4 sm:mb-6" itemProp="description">
                  💬 Contactez-nous directement sur WhatsApp, notre équipe vous répond en quelques minutes !
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a
                    href="https://wa.me/212702094593"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-green-600 font-bold rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl text-sm sm:text-base"
                    itemProp="url"
                  >
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 transition-transform group-hover:scale-110" />
                    <span>Ouvrir WhatsApp</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3 transition-transform group-hover:translate-x-1" />
                  </a>
                  
                  <a
                    href="tel:+212702094593"
                    className="inline-flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 border-2 border-white/30 text-white font-semibold rounded-lg sm:rounded-xl hover:bg-white/10 transition-all duration-300 text-sm sm:text-base"
                  >
                    📞 07 02 09 45 93
                  </a>
                </div>
              </div>
              
              {/* WhatsApp Mockup */}
              <div className="relative mt-6 lg:mt-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
                  <div className="bg-green-600 rounded-t-lg sm:rounded-t-xl p-2 sm:p-3 mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs sm:text-sm">FB</span>
                      </div>
                      <div>
                        <div className="text-white font-semibold text-xs sm:text-sm">Faucon Bleu</div>
                        <div className="text-green-100 text-xs">En ligne</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div className="bg-white/20 rounded-lg p-2 sm:p-3">
                      <p className="text-white text-xs sm:text-sm">
                        👋 Bonjour ! Comment puis-je vous aider ?
                      </p>
                      <div className="text-xs text-green-100 mt-1">Maintenant</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Avantages Faucon Bleu",
            "description": "Les avantages de choisir Faucon Bleu pour vos services automobiles",
            "provider": {
              "@type": "AutoRepair",
              "name": "Faucon Bleu",
              "logo": "https://fauconbleu.ma/logo.jpeg",
              "url": "https://faucon-bleu.ma"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Avantages et services",
              "itemListElement": features.map((feature, index) => ({
                "@type": "Offer",
                "position": index + 1,
                "name": feature.title,
                "description": feature.description
              }))
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Service",
              "telephone": "+212702094593",
              "availableLanguage": ["French", "Arabic"],
              "serviceChannel": {
                "@type": "ServiceChannel",
                "serviceType": "WhatsApp",
                "serviceUrl": "https://wa.me/212702094593"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(-5px) rotate(-5deg); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default WhyChooseSection;